import $ from 'jquery';
import React from 'react';
import {renderToStaticMarkup} from 'react-dom/server';

import {assert} from 'Asserts';
import {StringIDFolder} from 'constants/FolderConstants';
import {SECTIONS} from 'constants/NavConstants';
import * as SourceConstants from 'constants/SourceConstants';
import OnboardingDAO from 'dao/OnboardingDAO';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import {Entry} from 'notebook/ELNConstants';
import {getStore} from 'redux-stores/Store';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import NavStore from './NavStore';
import * as SidepanelActions from './sidepanel/SidepanelActions';
import SidepanelStore from './sidepanel/SidepanelStore';

const {PROJECT_TYPE} = SourceConstants;

const PADDING = 8;
const TOTAL_STEPS = 5;
const TRANSITION_EVENTS = 'webkitTransitionEnd otransitionend oTransitionEnd msTransitionEnd transitionend';

const IntroContent = (): JSX.Element => (
  <div className="navTour-text">
    <div className="navTour-title">Welcome to Benchling!</div>
    <p>Benchling lets you focus on the science you love.</p>
    <p>Let's take a tour of the highlights.</p>
  </div>
);
const intro = {
  clearOverlay: true,
  content: renderToStaticMarkup(<IntroContent />),
  extraPopoverClass: 'navTour--introPopover',
  hideArrow: true,
  html: true,
  hideTitle: true,
  forceCount: 0,
  forceTotal: TOTAL_STEPS,
  introStep: true,
  overlay: true,
  left: '50%',
  placement: 'top',
  showNext: true,
  title: 'Welcome to Benchling!',
  target: '.modal-container',
  top: '30%',
};

const NavContent = (): JSX.Element => (
  <div className="navTour-text">
    Navigate between Home and Projects, and easily Create files or Search, all in one place.{' '}
    <strong>Click the highlighted icon to close the side panel and give open files more space.</strong>
  </div>
);
const nav = {
  clearOverlay: true,
  content: renderToStaticMarkup(<NavContent />),
  html: true,
  forceCount: 1,
  forceTotal: TOTAL_STEPS,
  overlay: true,
  placement: 'right',
  showNext: true,
  target: '.js-navTour-nav',
  title: 'Seamless Navigation',
  top: PADDING,
  waitPromise: () => {
    const deferred = $.Deferred();
    const promise = deferred.promise();
    SidepanelActions.openSection(SECTIONS.HOME);
    deferred.resolve();
    return promise;
  },
};

const HomeContent = (): JSX.Element => (
  <div className="navTour-text">
    Your Home sidepanel gives you an <strong>overview of your day</strong>. See more details by clicking on
    the <BenchlingIcon icon="chevron" /> to switch to the expanded view.
  </div>
);
const home = {
  clearOverlay: true,
  content: renderToStaticMarkup(<HomeContent />),
  html: true,
  forceCount: 2,
  forceTotal: TOTAL_STEPS,
  includeTargetMargin: false,
  overlay: true,
  placement: 'right',
  showNext: true,
  target: '.js-navTour-home',
  title: 'Home Sidepanel',
  top: PADDING,
  waitPromise: () => {
    const deferred = $.Deferred();
    const promise = deferred.promise();
    if (SidepanelStore.getIsExpanded()) {
      SidepanelActions.unexpand();
      $('.sidepanel').one(TRANSITION_EVENTS, () => deferred.resolve());
    } else {
      deferred.resolve();
    }
    return promise;
  },
};

const CalendarContent = (): JSX.Element => (
  <div className="navTour-text">
    This is your calendar, which you can use to plan experiments. See more details about experiments through
    the <strong>day, week, and month views</strong>. Get back to the sidepanel by clicking on
    <BenchlingIcon icon="chevron-left" />.
  </div>
);
const calendar = {
  clearOverlay: true,
  content: renderToStaticMarkup(<CalendarContent />),
  extraPopoverClass: 'navTour--calendarPopover',
  html: true,
  forceCount: 3,
  forceTotal: TOTAL_STEPS,
  includeTargetMargin: false,
  overlay: true,
  overlayPaddingBottom: PADDING,
  overlayPaddingLeft: PADDING,
  overlayPaddingRight: PADDING,
  overlayPaddingTop: PADDING,
  placement: 'left',
  showNext: true,
  target: '.js-navTour-calendar',
  title: 'Expanded Views',
  top: PADDING,
  waitPromise: () => {
    const deferred = $.Deferred();
    const promise = deferred.promise();
    SidepanelActions.expand();
    $('.sidepanel').one(TRANSITION_EVENTS, () => deferred.resolve());
    return promise;
  },
};

const ProjectsContent = (): JSX.Element => (
  <div className="navTour-text">
    Check off multiple files to move or delete them in the expanded project view.
  </div>
);
const projects = {
  clearOverlay: true,
  content: renderToStaticMarkup(<ProjectsContent />),
  html: true,
  forceCount: 4,
  forceTotal: TOTAL_STEPS,
  overlay: true,
  overlayPaddingBottom: PADDING,
  overlayPaddingLeft: PADDING,
  overlayPaddingRight: PADDING,
  overlayPaddingTop: PADDING,
  placement: 'bottom',
  showNext: true,
  target: '.js-navTour-expanded',
  top: 150,
  title: 'Easy Bulk Actions',
  waitPromise: () => {
    const deferred = $.Deferred();
    const promise = deferred.promise();
    const project = NavStore.getSources()?.find((source) => source.sourceType === PROJECT_TYPE);
    assert(project != null, 'NavTutorial expects a project to exist');
    getStore().dispatch(
      SidepanelReduxActions.listFolder({...(project.rootFolder as StringIDFolder), source: project})
    );
    deferred.resolve();
    return promise;
  },
};

const WorkspaceContent = (): JSX.Element => (
  <div className="navTour-text">
    Quickly access open files in the workspace. Split your workspace to see more details.
  </div>
);
// TODO(T4758): Change text to 'Split your workspace to see more details by clicking on [icon]'
const workspace = {
  clearOverlay: true,
  content: renderToStaticMarkup(<WorkspaceContent />),
  html: true,
  forceCount: 5,
  forceTotal: TOTAL_STEPS,
  overlay: true,
  placement: 'bottom',
  showNext: false,
  target: '.js-navTour-workspace',
  title: 'Accessible Workspace',
  waitPromise: () => {
    const deferred = $.Deferred();
    const promise = deferred.promise();
    SidepanelActions.unexpand();
    // Need to show a loading screen while sample entry is being fetched. This then gets hidden when
    // FileBrowserActions.openFile is done.
    $('#fileLoading').removeClass('hidden');
    OnboardingDAO.getOrCreateSampleEntry().done(({id}: Entry) => {
      FileBrowserActions.openFile(id);
      FileBrowserActions.addToWorkspace(id, undefined);
      deferred.resolve();
    });
    return promise;
  },
};

const STEPS = [intro, nav, home, calendar, projects, workspace];
export const NavTutorialForNewUsers = STEPS;
